import { useState, useEffect, useCallback } from 'react'
import { authenticatedFetchJson } from '../services/api/apiClient'
import { apiEndpoints } from '../consts'
import logger from '../utils/logger'

/**
 * Hook for fetching billing plan data + scheduled changes for a campaign.
 * Also resolves the billing_customer_id needed for creating new plans.
 * @param {string|number} campaignId
 */
const useBillingPlanData = (campaignId) => {
  const [plans, setPlans] = useState([])
  const [scheduledChanges, setScheduledChanges] = useState([])
  const [campaignName, setCampaignName] = useState(null)
  const [billingCustomerId, setBillingCustomerId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchPlans = useCallback(async () => {
    if (!campaignId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const url = `${apiEndpoints.billingPlans}?campaign_id=${campaignId}`
      const response = await authenticatedFetchJson(url)
      const planList = response?.data || []
      setPlans(planList)
      setCampaignName(planList[0]?.campaign_name || null)
      setBillingCustomerId(planList[0]?.billing_customer_id || null)

      // Fetch scheduled changes from the active plan detail
      const activePlan = planList.find(p => p.is_active)
      if (activePlan) {
        const detailResponse = await authenticatedFetchJson(`${apiEndpoints.billingPlans}/${activePlan.id}`)
        setScheduledChanges(detailResponse?.data?.scheduled_changes || [])
      } else {
        setScheduledChanges([])
      }

      // If no plans, try to get billing_customer_id from billing customers API
      if (planList.length === 0) {
        try {
          const custResponse = await authenticatedFetchJson(`${apiEndpoints.billingCustomers}`)
          const customers = custResponse?.data || []
          // Match by campaign_id first, then fall back to any customer with same account
          const exactMatch = customers.find(c => String(c.campaign_id) === String(campaignId))
          if (exactMatch) {
            setBillingCustomerId(exactMatch.id)
            setCampaignName(exactMatch.account_name)
          } else if (customers.length > 0) {
            // Fallback: use first customer (for accounts where campaign_id doesn't match first)
            setBillingCustomerId(customers[0].id)
            setCampaignName(customers[0].account_name)
          }
        } catch (_) { /* ignore — billing customer lookup is best-effort */ }
      }
    } catch (err) {
      logger.error('Failed to fetch billing plans:', err)
      setError(err.message || 'Failed to load billing plans')
    } finally {
      setLoading(false)
    }
  }, [campaignId])

  useEffect(() => {
    fetchPlans()
  }, [fetchPlans])

  return {
    plans,
    scheduledChanges,
    campaignName,
    billingCustomerId,
    loading,
    error,
    refetch: fetchPlans
  }
}

export default useBillingPlanData
